<template>
	<v-container class="pa-1 d-flex flex-column align-center">
		<v-btn :disabled="isDisabled" @click="buttonPressed()">
			<v-icon :class="color" icon="mdi-circle" size="small"></v-icon>
		</v-btn>
		{{ text }}
	</v-container>
</template>

<script>
/* Enums */
import EventStatus from "@/enums/EventStatus";
import SequencerModes from "@/enums/SequencerModes";

/* Libraries */
import emitter from "@/emitter";

const SEQUENCER_MODES_NAME = {
	1: "Auto",
	2: "Manual",
	3: "LiveAssist"
};
export default {
	name: "TopButton",

	props: ["text", "position", "channelData"],

	data() {
		return {
			audioRef: null,
			audioOutEnabled: false,
			auditionAudioWidgetMidiBC: null
		};
	},

	computed: {
		isUserMicOn() {
			return this.$store.getters.isUserMicOn(this.channelData.peerID);
		},

		isUserOnAir() {
			return this.$store.getters.isUserOnAir(this.channelData.peerID);
		},

		audioOutputType() {
			if (this.channelData.mode === "hotkey_control") {
				return this.$store.getters.getOutputType(this.channelData.channelNumber);
			}
			return null;
		},

		auditionAudioIsOpen() {
			return (
				this.channelData?.event?.EventID &&
				this.$store.getters.auditionAudioIsOpen(this.channelData?.event?.EventID)
			);
		},

		isDisabled() {
			return this.position === "button2" && this.channelData.mode === "master";
		},

		color() {
			if (this.channelData.mode === "master") {
				if (this.isDisabled) {
					return "black";
				}
				if (this.channelData.sequencerMode === SequencerModes.Auto) {
					return "green";
				} else if (this.channelData.sequencerMode === SequencerModes.Manual) {
					return "red";
				} else if (this.channelData.sequencerMode === SequencerModes.LiveAssist) {
					return "blue";
				}
			} else if (this.channelData.mode === "track_control") {
				if (this.position === "button1") {
					if (this.channelData.trackStatus === EventStatus.Current) {
						return "white";
					} else if (this.channelData.trackStatus) {
						return "grey";
					} else {
						return "black";
					}
				} else if (this.position === "button2") {
					if (this.auditionAudioIsOpen) {
						return "white";
					} else {
						return "black";
					}
				}
			} else if (this.channelData.mode === "hotkey_control") {
				if (this.position === "button1") {
					return this.audioOutputType === "hotkey" ? "white" : "black";
				} else if (this.position === "button2") {
					return this.audioOutputType === "audition" ? "white" : "black";
				}
			} else if (this.channelData.mode === "mic_control") {
				if (this.position === "button1") {
					// PGM
					if (this.isUserOnAir) {
						return "white";
					} else {
						return "black";
					}
				} else if (this.position === "button2") {
					// CUE
					if (this.isUserMicOn) {
						return "white";
					} else {
						return "black";
					}
				}
			}
			return null;
		},

		clientId() {
			return this.$store.getters.clientId();
		},

		selectedStationID() {
			//Midi UI is available in live mode only
			return this.$store.getters.liveModeSelectedStationID();
		},

		availableModes() {
			return this.$store.getters.liveModeSelectedStation()?.AvailableModes;
		}
	},

	watch: {
		color(value) {
			if (!this.isDisabled) {
				this.setControllerColor(value);
			} else {
				this.setControllerColor("black");
			}
		},

		"channelData.sequencerMode"(value, previousValue) {
			if (this.channelData.mode === "master" && value !== previousValue) {
				this.setControllerSequencerMode(SEQUENCER_MODES_NAME[value]);
			}
		}
	},

	mounted() {
		setTimeout(this.initializeButton, 500);

		if (this.channelData.mode === "track_control" && this.position === "button2") {
			this.auditionAudioWidgetMidiBC = new BroadcastChannel(
				`audition-audio-widget-midi-channel-${this.clientId}`
			);
			this.auditionAudioWidgetMidiBC.addEventListener("message", message => {
				if (message.data.type === "closeAuditionAudio") {
					this.auditionAudioIsOpen = false;
				}
			});
		}

		if (this.channelData.mode === "hotkey_control") {
			emitter.on("soundCardChanged", this.soundCardChanged);
		}

		emitter.on("button:pressed", this.buttonPressed);
		emitter.on("reinitializeMIDI", this.initializeButton);
		emitter.on("updateControllerColor", this.updateControllerColor);
	},

	beforeUnmount() {
		if (this.auditionAudioWidgetMidiBC) {
			this.auditionAudioWidgetMidiBC.close();
		}

		emitter.off("button:pressed", this.buttonPressed);
		emitter.off("reinitializeMIDI", this.initializeButton);
		emitter.off("updateControllerColor", this.updateControllerColor);
	},

	methods: {
		initializeButton() {
			if (!this.isDisabled) {
				emitter.emit("onSetButtonColor", {
					assignedChannel: this.channelData.assignment,
					button: this.position,
					color: this.color
				});
			} else {
				emitter.emit("onSetButtonColor", {
					assignedChannel: this.channelData.assignment,
					button: this.position,
					color: "black"
				});
			}
		},

		buttonPressed(ev) {
			if (ev && this.channelData.channelNumber !== ev.channel) {
				return;
			}

			if (this.channelData.mode === "master" && (ev ? ev.mode === "master" : true)) {
				if (this.position === "button1" && (ev ? ev.button === "button1" : true)) {
					const currentModeIndex = this.availableModes.findIndex(m => m === this.channelData.sequencerMode);
					this.setSequencerMode(this.availableModes[(currentModeIndex + 1) % this.availableModes.length]);
				}
			} else if (this.channelData.mode === "track_control" && (ev ? ev.mode === "track_control" : true)) {
				if (this.position === "button2" && (ev ? ev.button === "button2" : true)) {
					this.handleAuditionAudio();
				}
			} else if (this.channelData.mode === "mic_control" && (ev ? ev.mode === "mic_control" : true)) {
				if (this.position === "button1" && (ev ? ev.button === "button1" : true)) {
					this.toggleUserAirState();
				} else if (this.position === "button2" && (ev ? ev.button === "button2" : true)) {
					this.toggleUserMic();
				}
			} else if (this.channelData.mode === "hotkey_control" && (ev ? ev.mode === "hotkey_control" : true)) {
				if (this.position === "button1" && (ev ? ev.button === "button1" : true)) {
					this.outputHotkeySoundCard();
				} else if (this.position === "button2" && (ev ? ev.button === "button2" : true)) {
					this.outputAuditionSoundCard();
				}
			}
		},

		setMicOutput() {
			navigator.mediaDevices
				.getUserMedia({
					audio: true
				})
				.then(stream => {
					if (window.stream) {
						window.stream.getAudioTracks().forEach(track => track.stop());
						window.stream = null;
					} else {
						const audio = document.createElement("audio");
						audio.controls = true;
						audio.autoplay = true;
						window.stream = stream;
						audio.srcObject = stream;
					}
				});
		},

		handleAuditionAudio() {
			this.$store.commit("setAuditionAudioIsOpen", {
				eventID: this.channelData.event.EventID,
				status: !this.auditionAudioIsOpen
			});
		},

		toggleUserMic() {
			this.channelData.mainMidiBC.postMessage({
				type: "toggleUserMic",
				on: !this.isUserMicOn,
				user: this.channelData.peerID
			});
		},

		toggleUserAirState() {
			this.channelData.mainMidiBC.postMessage({
				type: "toggleUserAirState",
				on: !this.isUserOnAir,
				user: this.channelData.peerID
			});
		},

		setSequencerMode(mode) {
			emitter.emit("midi:setSequencerMode", mode);
		},

		outputHotkeySoundCard() {
			if (this.outputSoundCard !== "hotkey") {
				this.outputSoundCard = "hotkey";
				this.channelData.hotkeysMidiBC.postMessage({
					type: "setHotkeyAudioOutput",
					bank: this.channelData.channelNumber,
					value: this.outputSoundCard
				});
				emitter.emit("soundCardChanged", {
					bank: this.channelData.channelNumber,
					value: this.outputSoundCard
				});
			}
		},

		outputAuditionSoundCard() {
			if (this.outputSoundCard !== "audition") {
				this.outputSoundCard = "audition";
				this.channelData.hotkeysMidiBC.postMessage({
					type: "setHotkeyAudioOutput",
					bank: this.channelData.channelNumber,
					value: this.outputSoundCard
				});
				emitter.emit("soundCardChanged", {
					bank: this.channelData.channelNumber,
					value: this.outputSoundCard
				});
			}
		},

		soundCardChanged(bank, value) {
			if (this.channelData.channelNumber === bank) {
				this.outputSoundCard = value;
			}
		},

		updateControllerColor(ev) {
			if (this.channelData.assignment === ev.newlyAssignedChannel) {
				this.setControllerColor(this.color);
			}
		},

		// ================================= MIDI Controller ==================================== //

		setControllerSequencerMode(mode) {
			if (this.channelData.assignment) {
				emitter.emit("onSetChannelName", {
					channel: parseInt(this.channelData.assignment.slice(this.channelData.assignment.length - 1)) - 1,
					channelName: mode
				});
			}
		},

		setControllerColor(color) {
			emitter.emit("onSetButtonColor", {
				assignedChannel: this.channelData.assignment,
				button: this.position,
				color: color
			});
		}
		// ================================= MIDI Controller END ================================ //
	}
};
</script>

<style scoped lang="scss">
.v-btn {
	background-color: #777777;
	transition: all 0.15s ease-in-out;
	box-shadow:
		inset 0 1px 2px rgba(255, 255, 255, 0.3),
		inset 0 -2px 3px rgba(0, 0, 0, 0.4),
		0 3px 6px rgba(0, 0, 0, 0.5);

	&:active {
		transform: translateY(2px);
		box-shadow:
			inset 0 2px 4px rgba(0, 0, 0, 0.6),
			0 1px 2px rgba(0, 0, 0, 0.2);
	}

	.disabled {
		height: 4%;
		width: 37%;

		.v-icon {
			color: black;
		}
	}

	.green {
		color: $ihm-green;
	}

	.red {
		color: $ihm-red2;
	}

	.blue {
		color: $ihm-blue;
	}

	.white {
		color: $ihm-white;
	}

	.grey {
		color: #e0a0a0;
	}

	.black {
		color: $ihm-grey-2;
	}
}
</style>
